import { useCallback, useEffect, useMemo } from 'react';
import find from 'lodash/find';
import isEmpty from 'lodash/isEmpty';

import {
  MultiSelectChangeCallbackType,
  MultiSelectDataType,
  MultiSelectExtendedDataType,
  MultiSelectInputChangeCallbackType
} from '../../../../../../../../helpers/MultiSelect/types';
import { UserUUID } from '../../../../../../../users/usersTypes';
import { FetchSelectFieldUserQueryResponse } from '../../../../../../../users/queries/fetchSelectFieldUser.query';
import { FetchCacheTeamsUsersQueryResponse } from '../../../../../../queries/fetchCacheTeamsUsers.query';
import {
  FetchTeamsUsersCacheKey,
  TeamsUserUserID
} from '../../../../../../teamsUsersTypes';
import { TeamsUserIdSelectFieldTeamsUserType } from '../../TeamsUserIdSelectFieldControl.types';
import { TeamNanoID } from '../../../../../../../teams/teamsTypes';

import { FETCH_SELECT_FIELD_USER_QUERY } from '../../../../../../../users/queries/fetchSelectFieldUser.query';
import { FETCH_CACHE_TEAMS_USERS_QUERY } from '../../../../../../queries/fetchCacheTeamsUsers.query';

import { useUser } from '../../../../../../../users/hooks/useUser';
import { useCacheTeamsUsers } from '../../../../../../hooks/useCacheTeamsUsers';

import { UserCache } from '../../../../../../../users/UserCache';

export interface TeamsUserIdSelectFieldControlOptions {
  afterChange?: (teamsUser: TeamsUserIdSelectFieldTeamsUserType) => void;
  cacheKey: FetchTeamsUsersCacheKey;
  name: string;
  onChange: (value: TeamsUserUserID) => void;
  teamNanoId?: TeamNanoID;
  value: TeamsUserUserID;
}

function useTeamsUserIdSelectFieldControl({
  afterChange,
  cacheKey,
  name,
  onChange,
  teamNanoId,
  value
}: TeamsUserIdSelectFieldControlOptions) {
  const {
    teamsUsers,
    teamsUsersFetched,
    teamsUsersLoading,
    teamsUsersError,
    changeTeamsUsersFilters,
    teamsUsersIsPlaceholderData,
    loadMoreTeamsUsers,
    teamsUsersFetchingNextPage
  } = useCacheTeamsUsers<FetchCacheTeamsUsersQueryResponse>({
    cacheKey,
    query: FETCH_CACHE_TEAMS_USERS_QUERY,
    initialFilters: {
      teamNanoId: { eq: teamNanoId },
      userBlocked: false
    },
    trackTotalHits: true
  });

  const {
    user: selectedUser,
    userLoading: selectedUserLoading,
    userFetched: selectedUserFetched,
    userError: selectedUserError
  } = useUser<FetchSelectFieldUserQueryResponse>({
    cacheKey: UserCache.selectedUserSelectFieldCacheKey(name),
    query: FETCH_SELECT_FIELD_USER_QUERY,
    uuid: value as UserUUID,
    options: {
      enabled: !isEmpty(value),
      enabledPlaceholder: !isEmpty(value)
    }
  });

  const data = useMemo<MultiSelectExtendedDataType[]>(() => {
    return teamsUsers.map((teamsUser) => ({
      value: teamsUser.user?.id,
      image: teamsUser.user?.image?.file,
      label: teamsUser.user?.fullName
    }));
  }, [teamsUsers]);

  const handleInputChange = useCallback<MultiSelectInputChangeCallbackType>(
    (input) =>
      changeTeamsUsersFilters(
        { name: { ilike: input } },
        input ? [] : ['name']
      ),
    [changeTeamsUsersFilters]
  );

  const selectedValue = useMemo<MultiSelectExtendedDataType>(
    () =>
      selectedUser
        ? {
            value: selectedUser.id,
            image: selectedUser.image?.file,
            label: selectedUser.fullName
          }
        : null,
    [selectedUser]
  );

  const handleChange = useCallback<MultiSelectChangeCallbackType>(
    (updateValue: MultiSelectDataType) => {
      const updatedTeamsUserUserId = updateValue?.value as TeamsUserUserID;

      const updatedTeamsUser = find(
        teamsUsers,
        (teamsUser) => teamsUser.user?.id === updatedTeamsUserUserId
      );

      onChange(updatedTeamsUserUserId);
      afterChange?.(updatedTeamsUser?.user);
    },
    [afterChange, onChange, teamsUsers]
  );

  useEffect(() => {
    if (teamNanoId) {
      changeTeamsUsersFilters({ teamNanoId: { eq: teamNanoId } });
    }
  }, [teamNanoId, changeTeamsUsersFilters]);

  return {
    data,
    handleChange,
    handleInputChange,
    loadMoreTeamsUsers,
    selectedUserError,
    selectedUserFetched,
    selectedUserLoading,
    selectedValue,
    teamsUsers,
    teamsUsersError,
    teamsUsersFetched,
    teamsUsersIsPlaceholderData,
    teamsUsersLoading,
    teamsUsersFetchingNextPage
  };
}

export default useTeamsUserIdSelectFieldControl;
